import { CallMethodEnums } from '@/types/api';
import {
  Model, ModelApiRes, ModelCreateReq, ModelCreateRes, ModelDeleteReq, ModelDeleteRes, ModelInfoDetail, ModelInfoReq, ModelInfoRes, ModelInvokeApiReq, ModelInvokeApiRes, ModelInvokeArgs, ModelInvokeUrlOptions, ModelUpdateReq, ModelUpdateRes
} from '@/types/model';
import { envVarsProvider } from '@/utils/envVars/envVars';

import { call, callJson, executeFetch } from './utils';

const _buildModelUrl = ( urlRoute: string, options?: ModelInvokeUrlOptions ): string => {
  const urlBase = envVarsProvider.get( 'AIDPSTUDIO_API_URL' );
  const baseUrl = options?.full ? urlBase : '';

  return `${ baseUrl }${ urlRoute }`;
};

const _getModelInvokeUrl = ( options?: ModelInvokeUrlOptions ): string => {
  return _buildModelUrl( '/api/models/invoke', options );
};

export const modelsGet = async(): Promise<ModelApiRes> => {
  const response = await callJson<unknown, ModelInfoRes>( {
    isProtected: true,
    method: CallMethodEnums.GET,
    route: '/api/proxy/litellm/model/info'
  } );

  // Transform model/info response to Model type and filter blacklisted models
  const transformedModels = response.data
    .filter( ( model: ModelInfoDetail ) => model.model_info.db_model )
    .map( ( model: ModelInfoDetail ): Model => {
      const providerModel = model.litellm_params.model;
      const providerFromModel = providerModel.split( '/' )[0];

      const provider = model.litellm_params.custom_llm_provider || model.model_info.litellm_provider || providerFromModel;

      return {
        name: model.model_name,
        createdAt: model.model_info.created_at ?? '',
        updatedAt: model.model_info.updated_at ?? '',
        source: provider,
        description: model.model_info.description ?? '',
        endpoint: '',
        uuid: model.model_info.id,
        publicName: model.model_name,
        internalName: providerModel,
        provider: provider
      };
    } )
    .filter( ( model: Model ) => model.provider && ALLOWED_PROVIDERS.has( model.provider ) );

  return {
    models: transformedModels
  };
};

export const modelInvoke = async( args: ModelInvokeArgs ): Promise<ModelInvokeApiRes> => {
  const {
    conversation,
    generationParamsCustom,
    generationParamsDefault,
    modelName
  } = args;

  const body: ModelInvokeApiReq = {
    generationParamsCustom,
    inputs: {
      conversation
    },
    modelName,
    ...generationParamsDefault
  };

  return await call<ModelInvokeApiReq, ModelInvokeApiRes>( {
    body,
    isProtected: true,
    method: CallMethodEnums.POST,
    route: _getModelInvokeUrl()
  } );
};

export const modelInvokeStream = async( args: ModelInvokeArgs, signal?: AbortSignal ): Promise<ReadableStream<Uint8Array>> => {
  const {
    conversation,
    generationParamsCustom,
    generationParamsDefault,
    modelName
  } = args;

  const body: ModelInvokeApiReq = {
    generationParamsCustom,
    inputs: {
      conversation
    },
    modelName,
    ...generationParamsDefault
  };

  const response = await executeFetch( {
    route: '/api/models/invoke/stream',
    method: CallMethodEnums.POST,
    signal,
    isProtected: true,
    body,
    params: { full: true }
  } );


  if( !response.ok ) {
    throw new Error( `HTTP error! status: ${ response.status }` );
  }

  if( !response.body ) {
    throw new Error( 'No response body' );
  }

  return response.body;
};

export const modelCreate = async( body: ModelCreateReq ): Promise<ModelCreateRes> => {
  return await call<ModelCreateReq, ModelCreateRes>( {
    body,
    isProtected: true,
    method: CallMethodEnums.POST,
    route: '/api/proxy/litellm/model/new'
  } );
};

interface CredentialField {
  key: string;
  label: string;
  placeholder: string | null;
  tooltip: string | null;
  required: boolean;
  field_type: 'password' | 'text' | 'select';
  options: string[] | null;
  default_value: string | null;
}

export interface ProviderDetail {
  provider: string;
  provider_display_name: string;
  litellm_provider: string;
  credential_fields: CredentialField[];
  default_model_placeholder: string;
}

const ALLOWED_PROVIDERS = new Set<string>( [
  'anthropic',
  'openai',
  'gemini',
  'vertex_ai',
  'azure',
  'bedrock',
  'custom_openai',
  'ollama'
] as const );

export const getProviders = async(): Promise<ProviderDetail[]> => {
  const providers = await callJson<unknown, ProviderDetail[]>( {
    isProtected: true,
    method: CallMethodEnums.GET,
    route: '/api/proxy/litellm/public/providers/fields'
  } );

  // Filter to only allowed providers based on litellm_provider
  return providers.filter( provider => ALLOWED_PROVIDERS.has( provider.litellm_provider ) );
};

export const modelGetInfo = async( params?: ModelInfoReq ): Promise<ModelInfoRes> => {
  const queryParams = params?.litellm_model_id
    ? `?${ new URLSearchParams( { litellm_model_id: params.litellm_model_id } ).toString() }`
    : '';
  return await callJson<unknown, ModelInfoRes>( {
    isProtected: true,
    method: CallMethodEnums.GET,
    route: `/api/proxy/litellm/model/info${ queryParams }`
  } );
};

export const modelUpdate = async( body: ModelUpdateReq ): Promise<ModelUpdateRes> => {
  return await call<ModelUpdateReq, ModelUpdateRes>( {
    body,
    isProtected: true,
    method: CallMethodEnums.POST,
    route: '/api/proxy/litellm/model/update'
  } );
};

export const modelDelete = async( body: ModelDeleteReq ): Promise<ModelDeleteRes> => {
  return await call<ModelDeleteReq, ModelDeleteRes>( {
    body,
    isProtected: true,
    method: CallMethodEnums.POST,
    route: '/api/proxy/litellm/model/delete'
  } );
};
